import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Receipt, Undo2, FileText, HandCoins } from "lucide-react";
import { BRAND } from "@/lib/api";
import { toast } from "sonner";
import { useUserNames } from "@/lib/useUserNames";

const STATUS_TONE = {
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  partial: "border-amber-200 bg-amber-50 text-amber-700",
  due: "border-rose-200 bg-rose-50 text-rose-700",
  refunded: "border-purple-200 bg-purple-50 text-purple-700",
  cancelled: "border-zinc-300 bg-zinc-100 text-zinc-700",
  pending_settlement: "border-amber-300 bg-amber-50 text-amber-800",
  partial_settlement: "border-amber-300 bg-amber-50 text-amber-800",
  free: "border-cyan-200 bg-cyan-50 text-cyan-700",
};

export default function InvoiceView() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", mode: "cash", reason: "" });
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleForm, setSettleForm] = useState({
    received_amount: "", tds_amount: 0, panel_discount: 0, other_deduction: 0,
    deduction_note: "", mode: "bank_transfer", reference: "", settled_by_payer: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const nameOf = useUserNames();

  const load = () => api.get(`/billing/invoices/${id}`).then(({ data }) => setInv(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!inv) return <div className="text-sm text-ink-muted">Loading…</div>;

  const paid = Number(inv.amount_paid || 0);
  const alreadyRefunded = Number(inv.refund_amount || 0);
  const refundable = Math.max(0, paid - alreadyRefunded);
  const refunds = Array.isArray(inv.refunds) ? inv.refunds : [];
  const settlements = Array.isArray(inv.settlements) ? inv.settlements : [];
  const outstanding = Math.max(0, Number(inv.total || 0) - paid);
  const isPanelPending = ["pending_settlement", "partial_settlement"].includes(inv.status);

  const openRefund = () => {
    setForm({ amount: refundable.toFixed(2), mode: inv.payment_mode || "cash", reason: "" });
    setRefundOpen(true);
  };

  const openSettle = () => {
    setSettleForm({
      received_amount: outstanding.toFixed(2),
      tds_amount: 0, panel_discount: 0, other_deduction: 0,
      deduction_note: "",
      mode: "bank_transfer",
      reference: "",
      settled_by_payer: inv.panel_name || inv.tpa_name || inv.corporate_name || "",
      notes: "",
    });
    setSettleOpen(true);
  };

  // Live: total deductions applied against the bill.
  const settleDeductionsTotal = (
    parseFloat(settleForm.tds_amount || 0) +
    parseFloat(settleForm.panel_discount || 0) +
    parseFloat(settleForm.other_deduction || 0)
  );
  // Suggested net = outstanding − total deductions. We auto-update Received as
  // deductions change so the cashier doesn't need to re-type 7,800 manually.
  const suggestedReceived = Math.max(0, outstanding - settleDeductionsTotal);
  const settleAppliedAmount = parseFloat(settleForm.received_amount || 0) + settleDeductionsTotal;

  // Helper — update a deduction and auto-rebalance Received so applied == outstanding
  const updateDeduction = (field, val) => {
    const v = val === "" ? "" : Number(val);
    const next = { ...settleForm, [field]: v };
    const otherDed =
      ["tds_amount", "panel_discount", "other_deduction"].reduce(
        (s, k) => s + Number(k === field ? v || 0 : next[k] || 0), 0,
      );
    next.received_amount = Math.max(0, outstanding - otherDed).toFixed(2);
    setSettleForm(next);
  };

  const submitSettle = async () => {
    const amt = parseFloat(settleAppliedAmount.toFixed(2));
    if (!amt || amt <= 0) { toast.error("Enter a received amount or deduction > 0"); return; }
    if (amt > outstanding + 0.01) { toast.error(`Applied amount ₹${amt.toFixed(2)} cannot exceed outstanding ₹${outstanding.toFixed(2)}.`); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/billing/invoices/${inv.id}/settle`, {
        amount: amt,
        received_amount: parseFloat(settleForm.received_amount || 0),
        tds_amount: parseFloat(settleForm.tds_amount || 0),
        panel_discount: parseFloat(settleForm.panel_discount || 0),
        other_deduction: parseFloat(settleForm.other_deduction || 0),
        deduction_note: settleForm.deduction_note,
        mode: settleForm.mode,
        reference: settleForm.reference,
        settled_by_payer: settleForm.settled_by_payer,
        notes: settleForm.notes,
      });
      toast.success(`Receipt ${r?.receipt_no} · ₹${amt.toFixed(2)} settled`);
      setSettleOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitRefund = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a refund amount > 0"); return; }
    if (amt > refundable + 0.01) { toast.error(`Refund cannot exceed ₹${refundable.toFixed(2)}.`); return; }
    setBusy(true);
    try {
      const { data: ref } = await api.post(`/billing/invoices/${inv.id}/refund`, {
        amount: amt,
        mode: form.mode,
        reason: form.reason || "Refund",
      });
      toast.success(`Refund ${ref?.refund_no || ""} · ₹${amt.toFixed(2)} issued`);
      setRefundOpen(false);
      await load();
      if (ref?.refund_no) {
        window.open(`/print/refund-receipt/${inv.id}/${encodeURIComponent(ref.refund_no)}`, "_blank");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">Invoice</div>
          <h1 className="font-heading text-3xl font-semibold">{inv.invoice_no}
            <Badge variant="outline" className={`ml-3 ${STATUS_TONE[inv.status] || ""}`}>{inv.status}</Badge>
          </h1>
          <div className="mt-1 text-sm text-ink-muted">{inv.patient_name} · {inv.patient_uhid} · {(inv.created_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="flex gap-2">
          {isPanelPending && outstanding > 0 && (
            <Button
              onClick={openSettle}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="invoice-settle-btn"
            >
              <HandCoins size={14} className="mr-2" />Settle ₹{outstanding.toFixed(2)}
            </Button>
          )}
          {refundable > 0 && inv.status !== "cancelled" && (
            <Button
              variant="outline"
              onClick={openRefund}
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="invoice-refund-btn"
            >
              <Undo2 size={14} className="mr-2" />Refund
            </Button>
          )}
          <Link to={`/print/invoice/${inv.id}`} target="_blank">
            <Button className="bg-brand hover:bg-brand-700"><Printer size={14} className="mr-2" />Print invoice</Button>
          </Link>
        </div>
      </div>

      {/* Panel / TPA banner */}
      {isPanelPending && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm" data-testid="invoice-panel-banner">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold text-amber-900">
              Panel / TPA bill awaiting settlement
            </span>
            <span className="text-amber-800">
              · Payer: <b className="capitalize">{inv.payer_category || "panel"}</b>
              {inv.panel_name && <> · {inv.panel_name}</>}
              {inv.policy_no && <> · Policy {inv.policy_no}</>}
              {inv.tpa_name && <> · TPA {inv.tpa_name}</>}
            </span>
            <span className="ml-auto text-amber-900">
              Outstanding <b>₹ {outstanding.toFixed(2)}</b>
            </span>
          </div>
          <div className="mt-1 text-xs text-amber-800/80">
            No payment receipt has been issued yet. Click <b>Settle</b> when the panel transfers the amount —
            a new receipt number will be generated at that moment.
          </div>
        </div>
      )}

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST%</th><th className="px-3 py-2 text-right">Total</th></tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="px-3 py-2">{it.description}</td>
                  <td className="px-3 py-2 text-right">{it.qty}</td>
                  <td className="px-3 py-2 text-right">₹ {it.rate.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{it.gst_percent}%</td>
                  <td className="px-3 py-2 text-right font-medium">₹ {it.line_total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 grid gap-2 text-sm md:grid-cols-2">
          <div className="text-ink-muted">Notes: {inv.notes || "—"}</div>
          <div className="space-y-1 md:text-right">
            <div>Gross: ₹ {inv.gross.toFixed(2)}</div>
            <div>Discount: − ₹ {inv.discount_total.toFixed(2)}</div>
            <div>Tax: + ₹ {inv.tax_total.toFixed(2)}</div>
            <div className="font-heading text-lg text-brand">Total: ₹ {inv.total.toFixed(2)}</div>
            <div className="text-xs text-ink-muted">Paid ₹ {paid.toFixed(2)} via {inv.payment_mode} · Balance ₹ {inv.balance.toFixed(2)}</div>
            {alreadyRefunded > 0 && (
              <div className="text-xs font-medium text-rose-700">Refunded ₹ {alreadyRefunded.toFixed(2)} · Net ₹ {(paid - alreadyRefunded).toFixed(2)}</div>
            )}
          </div>
        </div>
        <div className="mt-6 border-t border-border pt-3 text-[11px] text-ink-muted"><Receipt size={11} className="mr-1 inline" />{BRAND.footer}</div>
      </Card>

      {/* Refund history with reprint links */}
      {refunds.length > 0 && (
        <Card className="overflow-hidden" data-testid="invoice-refund-history">
          <div className="border-b border-border bg-rose-50/40 px-4 py-2 text-sm font-semibold text-rose-800">
            Refund history ({refunds.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2 text-left">Refund No.</th>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Mode</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">By</th>
                <th className="px-3 py-2 text-right">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {refunds.map((r, i) => (
                <tr key={r.refund_no || i}>
                  <td className="px-3 py-2 font-mono text-xs">{r.refund_no || `#${i + 1}`}</td>
                  <td className="px-3 py-2 text-xs">{(r.refunded_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-right font-medium text-rose-700">₹ {Number(r.amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 capitalize">{r.mode || "cash"}</td>
                  <td className="px-3 py-2 text-xs">{r.reason || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.refunded_by_name || nameOf(r.refunded_by, r.refunded_by) || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <a href={`/print/refund-receipt/${inv.id}/${encodeURIComponent(r.refund_no || i)}`} target="_blank" rel="noreferrer" data-testid={`invoice-refund-reprint-${i}`}>
                      <Button size="sm" variant="outline"><FileText size={12} className="mr-1" />Reprint</Button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Settlement history */}
      {settlements.length > 0 && (
        <Card className="overflow-hidden" data-testid="invoice-settle-history">
          <div className="border-b border-border bg-amber-50/40 px-4 py-2 text-sm font-semibold text-amber-800">
            Settlement history ({settlements.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2 text-left">Receipt No.</th>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-right">Applied</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">TDS</th>
                <th className="px-3 py-2 text-right">Panel disc.</th>
                <th className="px-3 py-2 text-right">Other</th>
                <th className="px-3 py-2 text-left">Mode</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {settlements.map((s, i) => (
                <tr key={s.receipt_no || i}>
                  <td className="px-3 py-2 font-mono text-xs">{s.receipt_no || `#${i + 1}`}</td>
                  <td className="px-3 py-2 text-xs">{(s.settled_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-right font-medium text-amber-800">₹ {Number(s.amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">₹ {Number(s.received_amount ?? s.amount ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-xs text-rose-700">{s.tds_amount ? `₹ ${Number(s.tds_amount).toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-2 text-right text-xs text-rose-700">{s.panel_discount ? `₹ ${Number(s.panel_discount).toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-2 text-right text-xs text-rose-700">{s.other_deduction ? `₹ ${Number(s.other_deduction).toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-2 capitalize text-xs">{(s.mode || "bank_transfer").replace("_", " ")}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{s.reference || "—"}</td>
                  <td className="px-3 py-2 text-xs">{s.received_by_name || nameOf(s.received_by, s.received_by) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Settle dialog */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">Record panel / TPA settlement</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              Bill total ₹ {Number(inv.total || 0).toFixed(2)} · Already received ₹ {paid.toFixed(2)} · Outstanding <b>₹ {outstanding.toFixed(2)}</b>
            </div>

            {/* Deductions block — TDS / panel discount / other (top, so the
                cashier first removes deductions from the gross outstanding,
                then the Net received auto-balances below). */}
            <div className="rounded-md border border-rose-200 bg-rose-50/40 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-rose-800">Deductions by payer</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>TDS deducted</Label>
                  <Input type="number" value={settleForm.tds_amount}
                    onChange={e => updateDeduction("tds_amount", e.target.value)}
                    placeholder="0" data-testid="invoice-settle-tds" />
                </div>
                <div>
                  <Label>Panel discount</Label>
                  <Input type="number" value={settleForm.panel_discount}
                    onChange={e => updateDeduction("panel_discount", e.target.value)}
                    placeholder="0" data-testid="invoice-settle-panel-disc" />
                </div>
                <div>
                  <Label>Other deduction</Label>
                  <Input type="number" value={settleForm.other_deduction}
                    onChange={e => updateDeduction("other_deduction", e.target.value)}
                    placeholder="0" data-testid="invoice-settle-other-disc" />
                </div>
                <div className="col-span-3">
                  <Label>Deduction note</Label>
                  <Input value={settleForm.deduction_note} onChange={e => setSettleForm({ ...settleForm, deduction_note: e.target.value })}
                    placeholder="e.g. TDS 2% u/s 194J · panel write-off 5%" data-testid="invoice-settle-deduct-note" />
                </div>
              </div>
            </div>

            {/* Money received block — auto-balanced from deductions above */}
            <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Money received</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Net received in bank *</Label>
                  <Input type="number" value={settleForm.received_amount}
                    onChange={e => setSettleForm({ ...settleForm, received_amount: e.target.value })}
                    data-testid="invoice-settle-received" />
                  <div className="mt-1 text-[10px] text-emerald-800/80">
                    Suggested = Outstanding ₹{outstanding.toFixed(2)} − Deductions ₹{settleDeductionsTotal.toFixed(2)} = <b>₹{suggestedReceived.toFixed(2)}</b>
                  </div>
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={settleForm.mode} onValueChange={v => setSettleForm({ ...settleForm, mode: v })}>
                    <SelectTrigger data-testid="invoice-settle-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["bank_transfer", "cheque", "upi", "neft", "rtgs", "cash"].map(m => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference / UTR</Label>
                  <Input value={settleForm.reference} onChange={e => setSettleForm({ ...settleForm, reference: e.target.value })} placeholder="UTR / cheque no." data-testid="invoice-settle-ref" />
                </div>
              </div>
            </div>

            {/* Live applied-amount summary */}
            <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 p-2 text-xs">
              <div className="text-ink-muted">
                Applied against bill =
                Received <b>₹ {Number(settleForm.received_amount || 0).toFixed(2)}</b>
                {Number(settleForm.tds_amount) > 0 && <> + TDS ₹ {Number(settleForm.tds_amount).toFixed(2)}</>}
                {Number(settleForm.panel_discount) > 0 && <> + Panel disc ₹ {Number(settleForm.panel_discount).toFixed(2)}</>}
                {Number(settleForm.other_deduction) > 0 && <> + Other ₹ {Number(settleForm.other_deduction).toFixed(2)}</>}
              </div>
              <div className="font-heading text-base font-bold text-amber-900" data-testid="settle-applied-total">
                ₹ {settleAppliedAmount.toFixed(2)}
              </div>
            </div>
            {settleAppliedAmount > outstanding + 0.01 && (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800">
                Applied ₹ {settleAppliedAmount.toFixed(2)} exceeds outstanding ₹ {outstanding.toFixed(2)}. Reduce received amount or a deduction line.
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Settled by (payer)</Label>
                <Input value={settleForm.settled_by_payer} onChange={e => setSettleForm({ ...settleForm, settled_by_payer: e.target.value })} placeholder="e.g. Star Health Insurance" data-testid="invoice-settle-payer" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={settleForm.notes} onChange={e => setSettleForm({ ...settleForm, notes: e.target.value })} placeholder="optional" data-testid="invoice-settle-notes" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitSettle} className="bg-amber-600 hover:bg-amber-700" disabled={busy} data-testid="invoice-settle-submit">
              <HandCoins size={14} className="mr-2" />Record settlement & issue receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Issue refund</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-border bg-secondary/40 p-2 text-xs text-ink-muted">
              Paid ₹ {paid.toFixed(2)} · Already refunded ₹ {alreadyRefunded.toFixed(2)} · Refundable <b className="text-rose-700">₹ {refundable.toFixed(2)}</b>
            </div>
            <div>
              <Label>Refund amount *</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="invoice-refund-amount" />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={v => setForm({ ...form, mode: v })}>
                <SelectTrigger data-testid="invoice-refund-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "card", "upi", "netbanking", "cheque", "wallet"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="e.g. patient overpaid, service cancelled" data-testid="invoice-refund-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitRefund} className="bg-rose-600 hover:bg-rose-700" disabled={busy} data-testid="invoice-refund-submit">
              <Undo2 size={14} className="mr-2" />Issue refund & print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
